import type { LabeledValue } from 'antd/es/select'
import type { RawValue } from 'src/components/declare'
import type { EasyRemoteSearch } from 'src/components/common/declare'
import type { ListControlProps } from '../../declare'
import { useState, useEffect, useMemo, useRef, useContext } from 'react'
import { autorun } from 'mobx'
import { ListSearchContext, EasyRemoteSelect } from 'src/components'
import { appStore } from 'src/stores'
import { $api, $consts } from 'src/plugins'
import { getNavFieldsFromNavField, getSaveFieldFromNavField, getRulesFromNavFilters } from 'src/components/root/helper'
import { initFilter, extendOrders, initGroup, extendGroups, getSingleRule, extendRules, extendInclude } from 'src/utils/common'
import { uuid, validGUID } from 'src/utils/tools'
import { set } from 'lodash'

const PaginationSelect = (props: ListControlProps) => {
  const search = useContext(ListSearchContext)

  // 配置的过滤字段是否变化，变化则需要重新加载分页组件
  const [refreshKey, setRefreshKey] = useState(uuid())
  useEffect(() => {
    let timer: any = -1
    const dispose = autorun(() => {
      const { Nav_Filters } = props.config
      const filters = getRulesFromNavFilters(Nav_Filters, {
        user: appStore.current.userinfo,
        formBase: search.formBase
      })
      clearTimeout(timer)
      timer = setTimeout(() => {
        filters.length && setRefreshKey(JSON.stringify(filters))
      }, 1000)
    })

    return () => {
      dispose()
      clearTimeout(timer)
    }
  }, [props.config.Nav_Filters])

  // 是否配置了默认值
  const hasDefaultValue = useMemo(() => {
    return props.config.DATA_VALUE !== undefined
  }, [])
  const isDefaultValueSet = useRef(false)
  const handleDefaultValueSet = () => {
    if (hasDefaultValue && !isDefaultValueSet.current) {
      isDefaultValueSet.current = true
      const labelField = optionLabelFields[0]
      const valueField = 'ID'
      const find = source.current.find(item => item[labelField] === props.config.DATA_VALUE)
      find && handleChange({ label: find[labelField], value: find[valueField] })
    }
    // 释放保持
    search.queryKeeping[props.config.QUERY_NAME]?.()
  }

  // 分页下拉数据
  const source = useRef([])
  const getSource = (params: EasyRemoteSearch) => {
    const {
      DATA_VALUE,
      INPUT_DATA_API: url,
      INPUT_FILTER_ORG_TYPE: OrgType,
      INPUT_FILTER_FIELD,
      INPUT_NAV_FIELD,
      Nav_Filters,
      INPUT_SORT_FIELD,
      INPUT_SORT_ORDER,
    } = props.config
    if (!url) return new Promise(resolve => { resolve(false) })

    const { OrgId } = appStore.current.headers
    const payload = initFilter({ OrgId, OrgType, PageIndex: params.current })
    // 存在多字段排序
    const Orders: { Field: string, Order: number }[] = []
    if (INPUT_SORT_FIELD) {
      const sorts = INPUT_SORT_FIELD.split(',')
      const orders = INPUT_SORT_ORDER?.split(',') ?? []
      sorts.forEach((field: string, index: number) => {
        Orders.push({ Field: field, Order: Number(orders[index] ?? 0) })
      })
      extendOrders(payload, Orders)
    }
    // 搜索过滤字段
    const filterFields: string[] = INPUT_FILTER_FIELD?.split(',') || ['NAME', 'CODE']
    const value = params.value ?? (hasDefaultValue && !isDefaultValueSet.current ? DATA_VALUE : '')
    const Groups: any = initGroup(false)
    String(value) && Groups.Rules.push(...filterFields.map(field => getSingleRule(field, 9, value as RawValue)))
    extendGroups(payload, [Groups])
    // navFields 导航字段
    const navFields = getNavFieldsFromNavField(INPUT_NAV_FIELD)
    navFields.length && extendInclude(payload, navFields)
    // 配置的过滤字段
    const filters = getRulesFromNavFilters(Nav_Filters, {
      user: appStore.current.userinfo,
      formBase: search.formBase
    })
    extendRules(payload, filters)

    return $api['app/getDataByPost'](payload, {
      url,
      fullData: true
    }).then((res: any) => {
      source.current = res?.Data ?? []
      return res
    }).finally(() => {
      handleDefaultValueSet()
    })
  }

  const handleChange = (data: LabeledValue | undefined) => {
    const extraFields = getSaveFieldFromNavField(data?.value, source.current, props.config)
    const { QUERY_NAME, IS_CUSTOM } = props.config
    // IS_CUSTOM 为自定义参数时，填充 Rules，否则填充 Groups
    const presetData = {}
    set(presetData, IS_CUSTOM ? 'Rules' : 'Groups', extraFields)
    // 表单数据
    props.onChange?.(data?.value, {
      [`${$consts['TABLE/LIST_SEARCH_EXTRA_DATA_PREFIX']}${QUERY_NAME}`]: presetData
    })
  }

  // 下拉显示字段，用于 Option 的 label 显示
  const optionLabelFields = useMemo(() => {
    return props.config.INPUT_SHOW_FIELD?.split(',') ?? ['NAME']
  }, [props.config.INPUT_SHOW_FIELD])

  // 回填显示字段，用于选中后回填回 Select 框
  const customLabelFields = useMemo(() => {
    return props.config.INPUT_LABEL_FIELD?.split(',')
  }, [props.config.INPUT_LABEL_FIELD])

  const selectedValue = useMemo(() => {
    if (props.value === undefined) return undefined
    const isValueGUID = validGUID(props.value)
    return { label: !isValueGUID ? props.value : undefined, value: props.value }
  }, [props.value])

  const { PROMPT_INFO } = props.config
  return (
    <EasyRemoteSelect
      key={refreshKey}
      forceRender={hasDefaultValue}
      value={selectedValue}
      remoteMethod={getSource}
      onChange={val => handleChange(val as LabeledValue | undefined)}
      fieldOption={{ labels: optionLabelFields, inputLabels: customLabelFields }}
      selectProps={{ placeholder: PROMPT_INFO }}
      style={{ width: 183 }}
    />
  )
}

export default PaginationSelect